import { geoNaturalEarth1, geoPath } from "d3-geo";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  MultiPolygon,
  Polygon,
} from "geojson";
import { feature } from "topojson-client";
import type { GeometryCollection, Topology } from "topojson-specification";
import worldAtlas from "world-atlas/countries-110m.json" with { type: "json" };

export const WORLD_MAP_WIDTH = 960;
export const WORLD_MAP_HEIGHT = 500;

export type WorldCountryFeature = Feature<
  Polygon | MultiPolygon,
  GeoJsonProperties
> & {
  readonly id: string | number;
};

type CountriesTopology = Topology<{
  readonly countries: GeometryCollection;
}>;

function numericId(value: unknown): string | null {
  if (typeof value === "number" && Number.isSafeInteger(value) && value >= 0) {
    return String(value).padStart(3, "0");
  }
  if (typeof value === "string" && /^\d{1,3}$/.test(value)) {
    return value.padStart(3, "0");
  }
  return null;
}

function hasCountryBoundary(
  candidate: Feature,
): candidate is WorldCountryFeature {
  return (
    numericId(candidate.id) !== null &&
    (candidate.geometry.type === "Polygon" ||
      candidate.geometry.type === "MultiPolygon")
  );
}

const topology = worldAtlas as unknown as CountriesTopology;
const collection = feature(
  topology,
  topology.objects.countries,
) as FeatureCollection;

export const WORLD_COUNTRY_FEATURES: readonly WorldCountryFeature[] =
  Object.freeze(collection.features.filter(hasCountryBoundary));

export const WORLD_COUNTRY_NUMERIC_IDS: ReadonlySet<string> = new Set(
  WORLD_COUNTRY_FEATURES.map((country) => numericId(country.id)!),
);

const worldCollection: FeatureCollection<Polygon | MultiPolygon> = {
  type: "FeatureCollection",
  features: [...WORLD_COUNTRY_FEATURES],
};

const projection = geoNaturalEarth1().fitExtent(
  [
    [8, 8],
    [WORLD_MAP_WIDTH - 8, WORLD_MAP_HEIGHT - 8],
  ],
  worldCollection,
);
const projectedPath = geoPath(projection);

export function worldCountryNumericId(country: WorldCountryFeature): string {
  return numericId(country.id)!;
}

export function worldCountryPath(country: WorldCountryFeature): string {
  return projectedPath(country) ?? "";
}

export function worldCountryCentroid(
  country: WorldCountryFeature,
): readonly [number, number] {
  const [x, y] = projectedPath.centroid(country);
  return [x, y];
}
